import { Tracing } from "@tento-core/tracing";
import { nonnull } from "@tento-core/assert";
import type { InstallPacer } from "../../core/install-pacer.ts";
/**
 * SourceRow (abstract) — role base for rows that own data.
 *
 * Reads + writes its slice of `sheet.stores.sourceCellData`. Two
 * concrete subclasses: `DataSource` (every user- or provider-backed
 * row across all (dtype, cardinality) combinations) and
 * `FormulaDataSource` (cells owned by the formula engine; writes
 * reject). Typed cell access for scalar dtypes lives on the
 * auto-hydrated `column.*` renderer child
 * (`hydrateDefaultRenderer`); typed access for obj dtypes goes
 * through facades (`row.as(FACADE_KEY)`).
 */

import type { Option } from "../../core/option.ts";
import {
  DateTime,
  type DateUnit,
  NaiveDate,
  Utc,
} from "@tento-chrono";
import { RangeBasedFetcher } from "@tento-core/range-based-fetcher";
import { DependencyAspect } from "@tento-core/dependency-graph";

import { CellEntry, type CellValue, type CalendarEvent, type CellCardinality } from "../../core/dtype.ts";
import type { RowCellData } from "../../stores/row-cell-data.ts";
import { Json } from "../../core/json.ts";
import { LedgerKeys } from "../../stores/ledger-keys.ts";
import { CellLane } from "../../stores/cell-lane.ts";
import { CellWriteCoalescer } from "../../stores/cell-write-coalescer.ts";
import { CellWrites } from "../../ledger/cell-writes.ts";
import { SdkRequestError } from "../../core/errors.ts";
import type { SheetsNetwork } from "../../wire/rows-network.ts";
import { Actor } from "../../tx/invalidation.ts";
import type { Durable } from "../../core/durable.ts";
import type { SourceDescriptor } from "../descriptor.ts";
import type { FacadeKey } from "../../facades/facade.ts";
import { isCanonicalKey } from "../../backends/canonical-backend.ts";
import { RowId } from "../../ids/row-id.ts";
import { ModelRow } from "../model-row.ts";
import { declareRowLoader } from "../row-load.ts";
import type { RowLocalId } from "../../ids/row-local-id.ts";
import { EventOrigin } from "../../tx/event-origin.ts";
import type { ModelSheet } from "../../sheet/rows-sheet.ts";
import { sheetInternals, sheetOf, type SourceRowCells } from "../../sheet/sheet-stores.ts";
import * as CellDataStores from "../../stores/source-row-cell-data-store.ts";
import type { Unsubscribe } from "../../backends/rows-backend.ts";
import { CellId } from "../../ids/ids.ts";
import { RowNetworkKey } from "../../ids/row-network-key.ts";
import { deepFrozen } from "../../core/frozen-collections.ts";
import { Internals } from "../../core/internals.ts";
import { EncodingRegistry } from "../../core/encoding-registry.ts";

/** One physical resident and the row contract under which its coverage applies. */
type SourceReadIdentity = {
  readonly resident: RowCellData;
  readonly backendKey: RowNetworkKey;
  readonly encoding: Option<string>;
  readonly cardinality: CellCardinality;
};

/** Closures keep each key paired with its own store and revision domain. */
type SourceReadAdmission = SourceReadIdentity & {
  readonly revision: number;
  readonly fetchId: RowId;
  readonly current: () => boolean;
  readonly check: () => void;
  readonly release: Unsubscribe;
  readonly install: (operation: () => void) => void;
  readonly dateWritten: (cell: CellEntry, since: Option<SourceRow.WriteRevisions>) => boolean;
  readonly customWritten: (entry: CellEntry.Timepoint, since: Option<SourceRow.WriteRevisions>) => boolean;
  readonly setDate: (cell: CellEntry, value: CellValue) => void;
  readonly setCustom: (entry: CellEntry.Timepoint, value: CellValue) => void;
};

/** `SourceRow`'s friend record (`Internals`): its constructor registers each instance. */
type SourceRowInternals = {
  readonly ownsCellValue: (row: SourceRow, value: object) => boolean;
  readonly cells: (row: SourceRow) => SourceRowCells;
  readonly writer: (row: SourceRow) => SourceRow.Writer;
  readonly peekCell: (row: SourceRow, date: NaiveDate.Partial, cellId: Option<CellId>) => Option<CellValue>;
  readonly dateCells: (row: SourceRow, range: Option<NaiveDate.Range>) => readonly CellEntry[];
  readonly installBatched: (
    row: SourceRow,
    range: NaiveDate.Range,
    cells: readonly CellEntry[],
    metadata: Option<Json>,
    since: SourceRow.WriteRevisions,
  ) => void;
  readonly noteFetched: (row: SourceRow, range: NaiveDate.Range) => void;
};
const sourceRowInternals = new Internals<SourceRow, SourceRowInternals>("SourceRow");

/**
 * **The writers of a source row** — its checked, durable and local cell
 * writes (`SourceRow.Writer`). Not on the row: a `SourceRow` a read hands out
 * (`handle.model`, `row.variant`, `liveRowOf`) carries no writer. The package's
 * own renderer rows and facades write through it; the SDK layer reaches it
 * through `@tento-lona/sheets/internal-sdk` (an app writes through the SDK
 * row's verbs), this repository's tests through
 * `@tento-lona/sheets/internal-testing`.
 */
export function sourceWriterOf(row: SourceRow): SourceRow.Writer {
  return sourceRowInternals.of(row).writer(row);
}

/**
 * Whether `value` is a copy `row`'s cell store (canonical or sheet-local) made
 * of a written value (`CellDataStore.ownsCellValue`): proof a value the row
 * hands out is its own decoded copy, not a caller's object or a proxy of one.
 * A friend function, not a member: a published row variant carries no member
 * beyond the reviewed reads (`tools/lint/variant-writers`).
 */
export function sourceRowOwnsCellValue(row: SourceRow, value: object): boolean {
  return sourceRowInternals.of(row).ownsCellValue(row, value);
}

/**
 * The source-row classes whose cells an engine owns, most specific first:
 * each declares itself once, from its static block
 * (`declareEngineOwnedCells`), with the refusal a write or clear of its
 * cells throws. Filled while the variant classes load and never after.
 */
const ENGINE_OWNED_CELLS: Array<{
  readonly owns: (row: SourceRow) => boolean;
  readonly refusal: (row: SourceRow, method: "write" | "clear") => string;
}> = [];

/**
 * Declare that the cells of `cls`'s rows are an engine's (a formula, a
 * computed row): a checked write or clear of one throws `refusal`. Called
 * once per class from its static block — the package's replacement for an
 * overridable `assertWritable`; not on a barrel.
 */
export function declareEngineOwnedCells<T extends SourceRow>(
  cls: abstract new (...args: never[]) => T,
  refusal: (row: T, method: "write" | "clear") => string,
): void {
  ENGINE_OWNED_CELLS.unshift({
    owns: (row) => row instanceof cls,
    refusal: (row, method) => row instanceof cls ? refusal(row, method) : "",
  });
}

/**
 * The raw cell value `row` holds for `date` (and `cellId`), untyped at this
 * layer — from the canonical store for a canonical-keyed row, else the
 * sheet's source-cell store. A friend of the package's variants (a formula
 * row's `peekValue`); not a member of the row, and not on a barrel.
 */
export function peekSourceCell(
  row: SourceRow,
  date: NaiveDate.Partial,
  cellId: Option<CellId>,
): Option<CellValue> {
  return sourceRowInternals.of(row).peekCell(row, date, cellId);
}

/**
 * The date-keyed cells `row` holds in `range` (`null`: every one), as the
 * store keeps them: frozen, shared, in read order, with no `kind` tag and no
 * per-read copy. The date-lane read of a hot path that needs no timepoint
 * cells (a formula reading one period of its source); every other reader
 * goes through `peekCells`. A friend of the package's formula runtime; not a
 * member of the row, and not on a barrel.
 */
export function peekDateCells(
  row: SourceRow,
  range: Option<NaiveDate.Range>,
): readonly CellEntry[] {
  return sourceRowInternals.of(row).dateCells(row, range);
}

/**
 * The base of a row that holds or computes data: its cells, reads and the
 * checked and durable cell writes.
 */
export abstract class SourceRow extends ModelRow {
  declare readonly descriptor: SourceDescriptor;

  static readonly #internals: SourceRowInternals = {
    installBatched: (row, range, cells, metadata, since) =>
      row.#installBatchedCellsSince(range, cells, metadata, since),
    writer: (row) => row.#writerFace(),
    peekCell: (row, date, cellId) => row.#peekCell(date, cellId),
    dateCells: (row, range) => row.#dateCells(range),
    cells: (row) => row.#cells,
    ownsCellValue: (row, value) => row.#ownsCellValue(value),
    noteFetched: (row, range) => row.#noteFetchedRange(range),
  };

  static {
    // A source row's value is its own cells: one range fetch. Its tree then
    // walks its children (renderer rows) as any row's does.
    declareRowLoader(SourceRow, {
      value: (row, cx) => row.#fetchRange(cx.range, cx.padding),
      children: null,
    });
  }

  /** This row's writer face (`sourceWriterOf`), built on first use. */
  #writer: Option<SourceRow.Writer> = null;

  #writerFace(): SourceRow.Writer {
    if (this.#writer != null) return this.#writer;
    const writer: SourceRow.Writer = Object.freeze({
      row: this,
      write: <V extends CellValue = CellValue>(
        date: NaiveDate.Partial,
        value: V,
        cellId: Option<CellId>,
        actor: Actor,
      ) => this.#write(date, value, cellId, actor),
      writeDurable: <V extends CellValue = CellValue>(
        date: NaiveDate.Partial,
        value: V,
        cellId: Option<CellId>,
        actor: Actor,
      ) => this.#writeDurable(date, value, cellId, actor),
      clear: (date: NaiveDate.Partial, cellId: Option<CellId>, actor: Actor) =>
        this.#clear(date, cellId, actor),
      clearDurable: (date: NaiveDate.Partial, cellId: Option<CellId>, actor: Actor) =>
        this.#clearDurable(date, cellId, actor),
      writeTimepointCell: (
        dateRange: DateTime.Range<Utc>,
        data: Option<Json | CellValue>,
        replaceCellId: Option<CellId>,
        actor: Actor,
      ) => this.#writeTimepointCell(dateRange, data, replaceCellId, actor),
      clearTimepointCell: (cellId: CellId, actor: Actor) => this.#clearTimepointCell(cellId, actor),
      moveTimepointCell: (
        oldRange: DateTime.Range<Utc>,
        newRange: DateTime.Range<Utc>,
        cellId: CellId,
        data: Option<Json | CellValue>,
        actor: Actor,
      ) => this.#moveTimepointCell(oldRange, newRange, cellId, data, actor),
      writeEvent: (ev: CalendarEvent, prev: Option<CalendarEvent>, actor: Actor) =>
        this.#writeEvent(ev, prev, actor),
      local: Object.freeze({
        clearCell: (date: NaiveDate.Partial, cellId: Option<CellId>, actor: Actor) =>
          this.#clearCellLocal(date, cellId, actor),
        clearEvent: (ev: CalendarEvent, actor: Actor) => this.#clearEventLocal(ev, actor),
      }),
      markFreshInRange: (range: NaiveDate.Range) => this.#markFreshInRange(range),
      reread: (range: DateTime.Range<Utc>) => this.#reread(range),
    });
    this.#writer = writer;
    return writer;
  }

  /**
   * The SDK's own actor for a take-back: when the backend refuses (or never
   * answers) a cell write, the cell goes back to what it showed before, and
   * that repaint is announced as `SourceRow.ACTOR` — never as the writer's
   * actor, so a surface that skips its own echo still repaints the take-back.
   */
  static readonly ACTOR: Actor = new Actor("source-row");

  /**
   * Cache of facades resolved on this row. Built lazily by `as()`
   * — entries appear on first lookup of a key. A `null` entry
   * means the factory was probed and didn't match (cached so we
   * don't re-probe). Absence means the key hasn't been queried.
   */
  readonly #facadeCache: Map<string, Option<unknown>> = new Map();

  constructor(id: RowLocalId, descriptor: SourceDescriptor, sheet: ModelSheet) {
    super(id, descriptor, sheet);
    sourceRowInternals.register(this, SourceRow.#internals);
  }

  /**
   * Canonical cell shape id for this source row (`meta.shape`, e.g.
   * `"task/v1"`), or `null` when the row carries no shape. Mirrors the
   * private `shapeForSourceRow` in `render-plugin-core` — the single
   * signal a source row's typed identity (task / event / weather / …)
   * is read from, used by chrome plugins to pick a shape-accurate icon.
   */
  get shape(): Option<string> {
    const shape = this.#cells.rowMetadata.get(this.id).meta?.shape;
    return typeof shape === "string" ? shape : null;
  }

  /**
   * Resolve a typed facade registered against this row. Looks up
   * the factory in the sheet's `FacadeRegistry` on first call and
   * builds; caches the result (including null misses) so repeated
   * lookups are O(1).
   */
  as<F>(key: FacadeKey<F>): Option<F> {
    if (this.#facadeCache.has(key.id)) {
      return (this.#facadeCache.get(key.id) as Option<F>) ?? null;
    }
    const factory = sheetOf(this).facades.get(key);
    // Frozen: the facade is shared by every reader of the row (its state is
    // `#`-private, which freezing leaves writable to the facade itself).
    const built = factory ? factory.build(this) : null;
    if (built != null) Object.freeze(built);
    this.#facadeCache.set(key.id, built);
    return built;
  }

  /**
   * If this row's `lookupKey` is canonical (recognized by
   * `isCanonicalKey` — bare `:~rest` or `[provider]:~rest`), the
   * row points at a sheet-invariant canonical row (provider-backed
   * dataSource, e.g. `[weather@lona.so]:~weather:{locationId}`).
   * Cell reads for such rows resolve from `sheet.canonicalCells`
   * instead of the sheet-local `sourceCellData`, so multiple sheets
   * sharing the same Client see the same cells. Returns `null`
   * when the row has no lookupKey or the key isn't canonical.
   *
   * Canonical rows are sheet-invariant; see
   * `.docs/content/cell-stores-and-handles.md`.
   */
  /**
   * Whether this row computes its cells in the containing sheet's context.
   * Derived results stay sheet-local even when the row has a canonical-looking
   * lookup key; backend/provider rows return false, including read-only ones.
   */
  get cellsDerived(): boolean {
    // A loop, not `some` with a closure over `this`: every read admission check asks (several times a cell installed).
    for (const owner of ENGINE_OWNED_CELLS) if (owner.owns(this)) return true;
    return false;
  }

  /**
   * The holding sheet's cell writer (`SourceRowCells`): the one capability
   * this row reaches past its sheet's public surface for — the cell stores,
   * write revisions, coalescer, ingestion gate and promote hook. The sheet
   * builds it once; the row reads it, never the sheet's internals or stores.
   */
  get #cells(): SourceRowCells {
    return sheetInternals(sheetOf(this)).sourceRowCells;
  }

  #canonicalKey(): Option<string> {
    if (this.cellsDerived) return null;
    const key = this.#cells.rowMetadata.get(this.id).lookupKey;
    return isCanonicalKey(key) ? key : null;
  }

  /**
   * The `RowLocalId` of the row whose cells this row reads from.
   *
   * For canonical-source projections — rows with a canonical
   * `lookupKey` like `:~google:authId:calendar:calId:events` — the
   * data lives at `canonicalCells[lookupKey]`. The projection row
   * is just a sheet-local handle pointing at the shared cell store;
   * its own `this.id` is a per-sheet projection id, distinct from
   * the canonical row's id.
   *
   * Writes must target the data-owning row; routing them through
   * the projection's local id loses the canonical resolution and
   * the cells-route handler rejects (no writable row at that
   * virtual id).
   *
   * For rows whose `lookupKey` isn't canonical (formula rows,
   * ephemeral rows, regular sheet-local sources), returns `this.id`
   * — the row owns its own data.
   */
  dataOwningRowId(): RowLocalId {
    const ck = this.#canonicalKey();
    if (ck == null) return this.id;
    return sheetOf(this).registry.resolveFromRawId(ck);
  }

  /**
   * The opaque origin to stamp on events produced from this row.
   *
   * Carries the data-owning row id (resolved from the row's *stored*
   * canonical key, never derived from the event's calendar identity),
   * so writes route back to the writable source row. The single seam a
   * future non-row-backed source would override. See `EventOrigin`.
   */
  eventOrigin(): EventOrigin {
    return EventOrigin.backingRow(this.dataOwningRowId());
  }

  /**
   * Every cell this row holds in `range` — date-keyed and timepoint cells in
   * ONE list, each tagged with its `kind`, the same shape as `@tento/lona`'s
   * `RowCellCache.cellsIn`. A caller never picks a lane, so it cannot read one and
   * miss a cell that routed to the other (principle P1).
   *
   * Date-keyed cells match `range` directly; timepoint cells match its UTC day
   * bounds (`range.withTime().withTz(Utc)`). `null`: every cell the row holds,
   * unbounded, for callers that scan the whole row.
   */
  peekCells(range: Option<NaiveDate.Range>): SourceRow.Cell[] {
    return SourceRow.tagged(
      this.peekDateCells(range),
      this.peekTimepointCells(range),
    );
  }

  /**
   * The date-keyed cells this row holds in `range` (`null`: every one) — the
   * `kind: "date"` lane of `peekCells`, as the store keeps them: frozen,
   * shared, in the store's order, with no per-read copy and no `kind` tag. A
   * hot read path (a frame reading a window) takes the lane it needs here;
   * `peekCells` is their tagged union.
   */
  peekDateCells(range: Option<NaiveDate.Range>): readonly CellEntry[] {
    return this.#dateCells(range);
  }

  /**
   * The timepoint cells this row holds that start inside `range`'s UTC day
   * bounds (`range.withTime().withTz(Utc)`; `null`: every one) — the
   * `kind: "timepoint"` lane of `peekCells`, as the store keeps them
   * (frozen, shared, no per-read copy).
   */
  peekTimepointCells(range: Option<NaiveDate.Range>): readonly CellEntry.Timepoint[] {
    return this.#timepointCells(range == null ? null : range.withTime().withTz(Utc));
  }

  /**
   * How many cells `peekCells(range)` would answer, per lane — with no cell
   * copied (a count of the store's own range reads).
   */
  countIn(range: Option<NaiveDate.Range>): SourceRow.CellCount {
    return {
      date: this.peekDateCells(range).length,
      timepoint: this.peekTimepointCells(range).length,
    };
  }


  /** The date-keyed lane, from the canonical or sheet-local store. */
  #dateCells(range: Option<NaiveDate.Range>): readonly CellEntry[] {
    this.#cells.finishRecompute(this.id);
    const ck = this.#canonicalKey();
    if (ck != null) return this.#cells.canonicalCells.cells(ck, range);
    return this.#cells.sourceCellData.cells(this.id, range);
  }

  /**
   * The timepoint (custom) lane — sticker overlays, scheduling slots — from
   * the same canonical / sheet-local store as `dateCells`.
   */
  #timepointCells(
    range: Option<DateTime.Range<Utc>>,
  ): readonly CellEntry.Timepoint[] {
    this.#cells.finishRecompute(this.id);
    const ck = this.#canonicalKey();
    if (ck != null) return this.#cells.canonicalCells.customCells(ck, range);
    return this.#cells.sourceCellData.customCells(this.id, range);
  }

  /**
   * A token that changes whenever this row's cells (either lane) may have
   * changed: its cell store's per-row data and that data's `revision`. Equal
   * tokens (element by element, by identity) mean the same cells. O(1): no
   * cell is read. A row with no cells yet answers `[null, 0]`.
   */
  cellsRevision(): readonly [Option<object>, number] {
    const ck = this.#canonicalKey();
    const data = ck != null ? this.#cells.canonicalCells.rowData(ck) : this.#cells.sourceCellData.rowData(this.id);
    return data == null ? [null, 0] : [data, data.revision];
  }

  /** `sourceRowOwnsCellValue`'s answer: the row's own cell store's copy ledger. */
  #ownsCellValue(value: object): boolean {
    const ck = this.#canonicalKey();
    if (ck != null) return this.#cells.canonicalCells.ownsCellValue(value);
    return this.#cells.sourceCellData.ownsCellValue(value);
  }

  /** True when this row has unconsumed cell writes in `range`. */
  anyStaleInRange(range: NaiveDate.Range): boolean {
    const ck = this.#canonicalKey();
    if (ck != null) return this.#cells.canonicalCells.anyStaleInRange(ck, range);
    return this.#cells.sourceCellData.anyStaleInRange(this.id, range);
  }

  /** Mark this row's cell writes in `range` consumed by derived readers. */
  #markFreshInRange(range: NaiveDate.Range): void {
    const ck = this.#canonicalKey();
    if (ck != null) {
      this.#cells.canonicalCells.markFreshInRange(ck, range);
      return;
    }
    this.#cells.sourceCellData.markFreshInRange(this.id, range);
  }

  // ── Custom (datetime) cell writes ───────────────────────────────
  //
  // The datetime write seam (sticker / scheduling). Writes go to the
  // SAME store reads come from (`canonicalCells` for `:~` rows), then
  // reconcile to server truth — the server is the cell-id authority, so
  // a create/move's final id comes back in the write response.

  /** Create or overwrite a datetime cell keyed by a `DateTime.Range`.
   *  `replaceCellId` requests the server's atomic move. Nothing is painted
   *  before the server answers; a refusal rejects with the backend's error.
   *  The server's answer is shown stamped with `actor`, the writer's. */
  #writeTimepointCell(
    dateRange: DateTime.Range<Utc>,
    data: Option<Json | CellValue>,
    replaceCellId: Option<CellId>,
    actor: Actor,
  ): Promise<void> {
    return this.#sendTimepointCell(dateRange, data, replaceCellId, null, actor);
  }

  /**
   * Move datetime cell `cellId` from `oldRange` to `newRange` with `data`:
   * painted at once — one write of the cell in the sheet's timepoint ledger
   * (`CellWrites.timepoint`), stamped with `actor`, so a drag
   * settles in the turn it was issued — then sent as the server's atomic
   * replace of the cell at `newRange` (the sheet's COW promote first). Its
   * answer settles the move: accepted, the answer is shown (unless a newer
   * write of the cell stands); refused (or unanswered), this move's own
   * paint is taken back — the cell shows the newest move or delete of it
   * still waiting, or where it was before them (announced as
   * `SourceRow.ACTOR`) — and it rejects with the backend's error.
   */
  #moveTimepointCell(
    oldRange: DateTime.Range<Utc>,
    newRange: DateTime.Range<Utc>,
    cellId: CellId,
    data: Option<Json | CellValue>,
    actor: Actor,
  ): Promise<void> {
    const move = this.#paintMove(oldRange, newRange, cellId, data, actor);
    return this.#sendTimepointCell(newRange, data, cellId, move, actor);
  }

  /**
   * Send a datetime cell write (`replaceCellId`: the server's atomic
   * replace) and show the server's answer stamped with `actor`. `move` is
   * the paint a move already showed (`#paintMove`): the answer settles it —
   * a refusal takes back that paint alone, an older answer never paints
   * over a newer write of the cell.
   */
  async #sendTimepointCell(
    dateRange: DateTime.Range<Utc>,
    data: Option<Json | CellValue>,
    replaceCellId: Option<CellId>,
    move: Option<CellWrites.Write<Option<CellEntry.Timepoint>>>,
    actor: Actor,
  ): Promise<void> {
    let resp: SheetsNetwork.CellWriteResponse;
    try {
      // The sheet's COW promote first: an ephemeral sheet's row is re-keyed
      // by it, and `#customWriteRowId` then names the promoted row.
      const promoting = this.#promoting();
      if (promoting != null) await promoting;
      resp = await sheetOf(this).backend.writeTimepointCell(
        this.#customWriteRowId(),
        dateRange,
        data,
        replaceCellId,
        null,
      );
    } catch (err) {
      if (move != null && replaceCellId != null) this.#takeBackTimepoint(replaceCellId, move);
      throw err;
    }
    if (move != null) {
      const ledger = this.#cellWrites.timepoint;
      const standing = ledger.standing(move);
      ledger.accept(move);
      // A newer write of the cell stands — still in flight, or accepted
      // first (answers reversed): this older answer never paints over it.
      if (!standing) return;
    }
    this.#reconcileCustomWrite(resp, replaceCellId, actor);
  }

  /** Delete a datetime cell by raw id. Optimistically removes it (so the day
   *  repaints immediately) before the round-trip; a refusal takes back only
   *  this delete — the cell shows the newest write of it still waiting, or
   *  what it showed before them, so a refused older delete never brings back
   *  a cell a newer delete removed (announced as `SourceRow.ACTOR`) — and
   *  rejects with the backend's error. The delete and the server's answer
   *  are shown stamped with `actor`, the writer's. */
  async #clearTimepointCell(cellId: CellId, actor: Actor): Promise<void> {
    const write = this.#beginTimepointWrite(cellId, null);
    this.#removeTimepointLocal(cellId, actor);
    let resp: SheetsNetwork.CellWriteResponse;
    try {
      const promoting = this.#promoting();
      if (promoting != null) await promoting;
      resp = await sheetOf(this).backend.clearTimepointCell(
        this.#customWriteRowId(),
        cellId,
        null,
      );
    } catch (err) {
      this.#takeBackTimepoint(cellId, write);
      throw err;
    }
    const ledger = this.#cellWrites.timepoint;
    const standing = ledger.standing(write);
    ledger.accept(write);
    // A newer write of the cell stands: its paint (or answer) is what shows.
    if (!standing) return;
    this.#reconcileCustomWrite(resp, cellId, actor);
  }

  /** Paint a move of `cellId` to `newRange` now (`#moveTimepointCell`): one
   *  write of the cell in the sheet's timepoint ledger, stamped with `actor`;
   *  the move's replace write settles it. */
  #paintMove(
    _oldRange: DateTime.Range<Utc>,
    newRange: DateTime.Range<Utc>,
    cellId: CellId,
    data: Option<Json | CellValue>,
    actor: Actor,
  ): CellWrites.Write<Option<CellEntry.Timepoint>> {
    const moved: CellEntry.Timepoint = { id: cellId, dtr: newRange, value: data ?? null, style: null };
    const write = this.#beginTimepointWrite(cellId, moved);
    // `removeCustom` fires `clear-custom` at the cell's CURRENT (old)
    // range — repainting the day it leaves; `setCustom` fires
    // `set-custom` at the new range.
    this.#removeTimepointLocal(cellId, actor);
    this.#setTimepointLocal(moved, actor);
    return write;
  }

  /**
   * Open a timepoint write of `cellId` in the sheet's timepoint ledger before
   * it paints `value` (`null`: the cell is removed): what the cell shows now
   * is what the refusal of the first pending write restores.
   */
  #beginTimepointWrite(
    cellId: CellId,
    value: Option<CellEntry.Timepoint>,
  ): CellWrites.Write<Option<CellEntry.Timepoint>> {
    const lane = this.#cellWrites.timepoint;
    const write = lane.begin(this.#timepointKey(cellId), this.#customCell(cellId), value);
    const authority = this.#cells.sourceCellData.ordinaryStore(this.id);
    if (authority != null) lane.hold(write, authority.retain(this.id));
    return write;
  }

  /** The ledger key of the custom cell `cellId` (its row, its id). */
  #timepointKey(cellId: CellId): string {
    return CellWriteCoalescer.key(RowNetworkKey.of(this.#customWriteRowId()), `tp:${cellId}`);
  }

  /**
   * Take back a refused timepoint write: when it is the newest write of the
   * cell still waiting, the cell shows the next newest one, or — none left —
   * what it showed before them, announced as `SourceRow.ACTOR`. An older
   * write's refusal, or one a newer acceptance already retired, changes
   * nothing.
   */
  #takeBackTimepoint(
    cellId: CellId,
    write: CellWrites.Write<Option<CellEntry.Timepoint>>,
  ): void {
    const shown = this.#cellWrites.timepoint.refuse(write);
    if (shown == null) return;
    this.#removeTimepointLocal(cellId, SourceRow.ACTOR);
    if (shown.value != null) this.#setTimepointLocal(shown.value, SourceRow.ACTOR);
  }

  /** The custom cell `id` as this row shows it, or `null`. */
  #customCell(id: string): Option<CellEntry.Timepoint> {
    return this.#timepointCells(null).find((c) => c.id === id) ?? null;
  }

  /**
   * The rows-side precondition of a timepoint write that reaches the
   * backend: on an ephemeral sheet, the owning handle's COW promote (the
   * sheet's promote hook), which the write awaits before it is sent — a
   * refusal rejects the write, whose paint the caller takes back. `null`
   * when there is nothing to wait for (the sheet is synced, or has no
   * owning handle), so a synced sheet's write reaches the backend in the
   * same turn it was issued.
   */
  #promoting(): Option<Promise<void>> {
    if (!sheetOf(this).ephemeral) return null;
    return this.#cells.promoteHook()?.() ?? null;
  }

  #customWriteRowId(): RowId {
    const ck = this.#canonicalKey();
    return ck != null ? RowId.of(ck) : RowId.fromLocalId(this.id);
  }

  #setTimepointLocal(entry: CellEntry.Timepoint, actor: Option<Actor>): void {
    const ck = this.#canonicalKey();
    if (ck != null) this.#cells.canonicalCells.setCustom(ck, entry, actor);
    else this.#cells.sourceCellData.setCustom(this.id, entry, actor);
  }

  #removeTimepointLocal(cellId: CellId, actor: Option<Actor>): void {
    const ck = this.#canonicalKey();
    if (ck != null) this.#cells.canonicalCells.removeCustom(ck, cellId, actor);
    else this.#cells.sourceCellData.removeCustom(this.id, cellId, actor);
  }

  /** Reconcile a server custom-cell write by raw id, stamped with the writer's `actor`. */
  #reconcileCustomWrite(
    resp: SheetsNetwork.CellWriteResponse,
    removeCellId: Option<CellId>,
    actor: Actor,
  ): void {
    if (removeCellId != null) this.#removeTimepointLocal(removeCellId, actor);
    const cells: CellEntry.Timepoint[] = [];
    for (const c of resp.cells) {
      const tp = CellEntry.Timepoint.tryFrom(c);
      if (tp != null) cells.push(tp);
    }
    for (const cell of cells) this.#setTimepointLocal(cell, actor);
  }

  /**
   * Lazy-init `RangeBasedFetcher` (`@tento-core/range-based-fetcher`).
   * Owns padding (default 28 days), in-flight coalescing, and
   * extend-only-uncovered-region semantics so per-row fetches dedup
   * across overlapping calls — important on the calendar render
   * path, where the renderer asks for one day at a time across N
   * calendars and would otherwise fire N × days backend requests
   * per viewport refresh.
   *
   * Lazy because most rows never call `fetchRange` (formula rows,
   * leaf data sources hydrated inline) — instantiating per row across
   * a large sheet is wasteful.
   */
  #rangeFetchers: Option<WeakMap<RowCellData, {
    readonly identity: SourceReadIdentity;
    readonly fetcher: RangeBasedFetcher<Utc>;
  }>> = null;

  #discardedRead(key: RowNetworkKey): SourceReadDiscardedError {
    return new SourceReadDiscardedError(key);
  }

  #admitRead(): SourceReadAdmission {
    const sheet = sheetOf(this);
    const internals = this.#cells;
    if (internals.isRetired()) throw this.#discardedRead(this.backendRowKey());
    const ck = this.#canonicalKey();
    const backendKey = this.backendRowKey();
    const fetchId = ck == null ? RowId.fromLocalId(this.id) : RowId.of(ck);
    // Metadata belongs to this projection's local id, even for canonical data.
    const localId = RowId.fromLocalId(this.id);
    const encoding = sheet.encodingForRowId(localId);
    const cardinality = this.descriptor.cellCardinality;
    const rowCurrent = (): boolean => !internals.isRetired()
      && sheet.peekRow(this.id) === this
      && this.#canonicalKey() === ck && this.backendRowKey() === backendKey
      && sheet.encodingForRowId(localId) === encoding
      && this.descriptor.cellCardinality === cardinality;
    if (!rowCurrent()) throw this.#discardedRead(backendKey);
    if (ck != null) {
      const store = this.#cells.canonicalCells;
      store.setCardinality(ck, cardinality);
      if (!rowCurrent()) throw this.#discardedRead(backendKey);
      const read = store.captureRead(ck);
      const current = (): boolean => read.current() && rowCurrent();
      return {
        revision: read.revision, resident: read.resident, backendKey, fetchId, encoding, cardinality,
        current,
        check: () => { if (!current()) throw this.#discardedRead(backendKey); },
        release: read.release,
        install: operation => store.asInstall(() => store.batch(operation)),
        dateWritten: (cell, since) => store.writtenLocallySince(ck, since == null ? read.revision : since.canonical, cell.date, cell.id ?? null),
        customWritten: (entry, since) => store.customWrittenLocallySince(ck, since == null ? read.revision : since.canonical, entry.id),
        setDate: (cell, value) => store.set(ck, cell.date, value, cell.id ?? null, null, null),
        setCustom: (entry, value) => store.setCustom(ck, { ...entry, value }, null),
      };
    }
    const store = this.#cells.sourceCellData;
    store.setCardinality(this.id, cardinality);
    if (!rowCurrent()) throw this.#discardedRead(backendKey);
    const read = store.captureRead(this.id);
    const current = (): boolean => read.current() && rowCurrent();
    return {
      revision: read.revision, resident: read.resident, backendKey, fetchId, encoding, cardinality,
      current,
      check: () => { if (!current()) throw this.#discardedRead(backendKey); },
      release: read.release,
      install: operation => store.asInstall(() => store.batch(operation)),
      dateWritten: (cell, since) => store.writtenLocallySince(this.id, since == null ? read.revision : since.local, cell.date, cell.id ?? null),
      customWritten: (entry, since) => store.customWrittenLocallySince(this.id, since == null ? read.revision : since.local, entry.id),
      setDate: (cell, value) => store.set(this.id, cell.date, value, cell.id ?? null, null, null),
      setCustom: (entry, value) => store.setCustom(this.id, { ...entry, value }, null),
    };
  }

  #sameRead(a: SourceReadIdentity, b: SourceReadIdentity): boolean {
    return a.resident === b.resident && a.backendKey === b.backendKey
      && a.encoding === b.encoding && a.cardinality === b.cardinality;
  }

  #fetcher(read: SourceReadAdmission): RangeBasedFetcher<Utc> {
    read.check();
    this.#rangeFetchers ??= new WeakMap();
    const held = this.#rangeFetchers.get(read.resident);
    if (held != null && this.#sameRead(held.identity, read)) return held.fetcher;
    const identity: SourceReadIdentity = {
      resident: read.resident, backendKey: read.backendKey,
      encoding: read.encoding, cardinality: read.cardinality,
    };
    const fetcher = new RangeBasedFetcher<Utc>(async utcRange => {
      const range = new NaiveDate.Range(utcRange.start.date, utcRange.end.date);
      // Rows of one sheet fetching one range in the same tick (a bind's
      // per-row loads) share batches; a lone fetch, or a row the batch did
      // not serve, fetches for itself as before.
      const gathered = await this.#cells.ensureBatchLoaded(range, identity.backendKey);
      if (gathered !== "installed") await this.#performFetch(range, identity);
      const sheet = sheetOf(this);
      const internals = this.#cells;
      if (internals.isRetired() || sheet.peekRow(this.id) !== this
        || this.backendRowKey() !== identity.backendKey
        || sheet.encodingForRowId(RowId.fromLocalId(this.id)) !== identity.encoding
        || this.descriptor.cellCardinality !== identity.cardinality) {
        throw this.#discardedRead(identity.backendKey);
      }
      const ck = this.#canonicalKey();
      const resident = ck == null ? this.#cells.sourceCellData.rowData(this.id)
        : internals.canonicalCells.rowData(ck);
      if (resident !== identity.resident) throw this.#discardedRead(identity.backendKey);
    });
    this.#rangeFetchers.set(read.resident, { identity, fetcher });
    return fetcher;
  }

  #installRead(
    read: SourceReadAdmission,
    cells: readonly CellEntry[],
    custom: readonly CellEntry.Timepoint[],
    encoding: Option<string>,
    since: Option<SourceRow.WriteRevisions>,
  ): void {
    // The one read rule (`EncodingRegistry.decodeRead`, as the sheet-wide
    // route decodes): a cell the decoder rejects is left out, not stored as
    // a null cell; a wire null stays a null cell. The read's rejections are
    // counted and traced once, against the row's backend key.
    const decoder = sheetOf(this).encodings.readOf(encoding, read.backendKey.toString());
    read.check();
    try {
      read.install(() => {
        read.check();
        for (const cell of cells) {
          read.check();
          if (read.dateWritten(cell, since)) continue;
          const value = decoder.decode(cell.value, cell.id ?? cell.date);
          read.check();
          if (value === EncodingRegistry.REJECTED || read.dateWritten(cell, since)) continue;
          read.setDate(cell, value);
          read.check();
        }
        for (const entry of custom) {
          read.check();
          if (read.customWritten(entry, since)) continue;
          const value = decoder.decode(entry.value, entry.id);
          read.check();
          if (value === EncodingRegistry.REJECTED || read.customWritten(entry, since)) continue;
          read.setCustom(entry, value);
          read.check();
        }
      });
    } finally {
      decoder.end();
    }
    read.check();
  }

  /**
   * Fetch this row's cells for `range` from the sheet backend and
   * persist them into the appropriate cell store (`canonicalCells`
   * for canonical keys, `sourceCellData` otherwise). Wire-format
   * payloads are decoded through `sheet.encodings` (e.g.
   * `event.v1` → canonical `CalendarEvent` shape) before storage so typed
   * facades like `EVENT_SHAPE.probe` accept them.
   *
   * Padding and coalescing live in the underlying
   * `RangeBasedFetcher` (default 28-day padding; in-flight requests
   * dedup; ranges fully covered by prior fetches resolve
   * immediately).
   */
  async #fetchRange(
    range: NaiveDate.Range,
    padding: Option<DateUnit>,
  ): Promise<void> {
    const read = this.#admitRead();
    let failure: Option<() => never> = null;
    try {
      read.check();
      const fetcher = this.#fetcher(read);
      const utcRange = range.withTime().withTz(Utc);
      // Omission retains the fetcher's existing 28-day default padding.
      if (padding == null) await fetcher.fetchRange(utcRange);
      else await fetcher.fetchRange(utcRange, padding);
      read.check();
    } catch (error) { failure = () => { throw error; }; }
    try { read.release(); }
    catch (error) { if (failure == null) failure = () => { throw error; }; }
    if (failure != null) failure();
    // A release observer may retire the model; completed coverage must not fulfill it.
    if (this.#cells.isRetired()) throw this.#discardedRead(read.backendKey);
  }

  /**
   * Re-read this row's cells over the days `range` spans from the sheet
   * backend — a failed write's recovery when the write may have stored
   * (`SdkRequestError.mayHaveStored`): the fetch coverage is dropped, so the
   * range is fetched again and installed as a fetch installs it.
   */
  async #reread(range: DateTime.Range<Utc>): Promise<void> {
    if (this.#cells.isRetired()) throw this.#discardedRead(this.backendRowKey());
    const ck = this.#canonicalKey();
    const resident = ck == null ? this.#cells.sourceCellData.rowData(this.id)
      : this.#cells.canonicalCells.rowData(ck);
    const held = resident == null ? null : this.#rangeFetchers?.get(resident) ?? null;
    if (held != null) held.fetcher.fetchedRange = null;
    await this.#fetchRange(new NaiveDate.Range(range.start.date, range.end.date.addDays(1)), null);
  }

  async #performFetch(range: NaiveDate.Range, expected: SourceReadIdentity): Promise<void> {
    const read = this.#admitRead();
    let pace: Option<InstallPacer.Lease> = null;
    let failure: Option<() => never> = null;
    try {
      read.check();
      if (!this.#sameRead(read, expected)) throw this.#discardedRead(read.backendKey);
      const sheet = sheetOf(this);
      const fetched = await sheet.backend.fetchCells(read.fetchId, range);
      read.check();
      await this.#cells.ingestionGate.whenReleased().promise;
      read.check();
      const pacer = this.#cells.installPacer();
      pace = pacer == null ? null : await pacer.admit(sheet);
      read.check();
      this.#installRead(read, fetched.cells, fetched.custom, read.encoding, null);
      const { FormulaEngine } = await import("../../formula/formula-engine.ts");
      read.check();
      FormulaEngine.recomputeDependents(sheet, this.id, range);
      read.check();
    } catch (error) { failure = () => { throw error; }; }
    try { read.release(); }
    catch (error) { if (failure == null) failure = () => { throw error; }; }
    try { pace?.settle(); }
    catch (error) { if (failure == null) failure = () => { throw error; }; }
    if (failure != null) failure();
    // A release observer may retire the model; completed coverage must not fulfill it.
    if (this.#cells.isRetired()) throw this.#discardedRead(read.backendKey);
  }

  /**
   * The wire-format rowKey this SourceRow fetches under — canonical
   * lookup-key when set (`:~google:…`), otherwise the row's local
   * UUID (`r_<hex>`). Mirrors the dispatch in `performFetch` so
   * batch callers (`ListDataSource.ensureLoaded`) and per-row callers
   * agree on the key the server keys per-rowkey auth on.
   */
  backendRowKey(): RowNetworkKey {
    const ck = this.#canonicalKey();
    return ck != null ? RowNetworkKey.fromWire(ck) : RowNetworkKey.of(RowId.fromLocalId(this.id));
  }

  /**
   * Install a batch-fetched cell list for `range`, exactly as `performFetch`
   * would have: decode through this row's encoding, write into the
   * appropriate store (canonical vs. sheet-local), and mark the row's
   * `RangeBasedFetcher` as having covered the range so a subsequent
   * `fetchRange()` does not re-issue a per-row HTTP call. `since` is the
   * sheet's write revisions when the request went out: any cell written
   * locally after it keeps its newer value (candidate D).
   *
   * Private: the package reaches it through the module friend
   * `installBatchedCells` (`Sheet._performPrefetch`,
   * `ListDataSource._performBatchFetch`). Not on `Backend` because the
   * decoding + store-routing logic lives on the row, not on the backend seam.
   */
  #installBatchedCellsSince(
    range: NaiveDate.Range,
    cells: readonly CellEntry[],
    metadata: Option<Json>,
    since: SourceRow.WriteRevisions,
  ): void {
    const ck = this.#canonicalKey();
    const residentCardinality = ck == null ? null
      : this.#cells.canonicalCells.rowData(ck)?.cardinality ?? null;
    // This scope qualifies immediate installation, not the earlier async batch.
    const read = this.#admitRead();
    let failure: Option<() => never> = null;
    try {
      read.check();
      const encoding = encodingFromMetadata(metadata) ?? read.encoding;
      const bumper = this.#cells.sourceCellData.ledgerBumper;
      const install = (): void => {
        read.check();
        this.#cells.withDeferredListeners(() => {
          if (ck != null) {
            Tracing.log("drive/authority/cardinality", { key: ck, row: this.id.serial, declared: this.descriptor.cellCardinality, resident: residentCardinality });
          }
          // Keep the original request's paired clocks, not this immediate scope's.
          this.#installRead(read, cells, [], encoding, since);
        });
        read.check();
        this.#fetcher(read).noteInstalled(range.withTime().withTz(Utc));
        read.check();
        bumper?.bumpOp(LedgerKeys.RANGE, DependencyAspect.VALUES, "range", {
          t: "range/commit",
          start: range.start.toString(),
          end: range.end.toString(),
        });
        read.check();
      };
      // Keep cell writes and the range commit in the existing one transaction.
      if (bumper != null) bumper.txScope("batched-cells", install);
      else install();
      read.check();
    } catch (error) { failure = () => { throw error; }; }
    try { read.release(); }
    catch (error) { if (failure == null) failure = () => { throw error; }; }
    if (failure != null) failure();
    // A release observer may retire the model; completed coverage must not fulfill it.
    if (this.#cells.isRetired()) throw this.#discardedRead(read.backendKey);
  }

  /**
   * Note `range` as read for this row with no read of its own: a sheet-wide
   * read answered the row over `range` and its cells are installed
   * (`SheetPrefetch.beginSheetRead`), so a later `fetchRange` inside it
   * issues nothing. Coverage only: the cells came in through the read.
   */
  #noteFetchedRange(range: NaiveDate.Range): void {
    const read = this.#admitRead();
    try {
      read.check();
      this.#fetcher(read).noteInstalled(range.withTime().withTz(Utc));
    } finally {
      read.release();
    }
  }

  /** Read the raw cell value for a date, untyped at this layer. */
  #peekCell(
    date: NaiveDate.Partial,
    cellId: Option<CellId>,
  ): Option<CellValue> {
    this.#cells.finishRecompute(this.id);
    const ck = this.#canonicalKey();
    if (ck != null) return this.#cells.canonicalCells.get(ck, date, cellId);
    return this.#cells.sourceCellData.get(this.id, date, cellId);
  }

  /**
   * Write a cell: applies optimistically to the local store, cascades
   * formula recomputes to every downstream formula row, then persists
   * through `sheet.backend.writeCell` and resolves on its verdict. A refusal
   * takes the paint back — the cell shows what it showed before this write
   * (or the newest write of it still waiting on its verdict), announced as
   * `SourceRow.ACTOR` — and rejects with the backend's error.
   *
   * For canonical-keyed rows the optimistic write lands on the
   * Client-scoped `canonicalCells` (the same store the read path
   * resolves from), and the backend write addresses the row by its
   * canonical lookup-key — keeping read and write paths in sync.
   * Sheet-local rows hit `sourceCellData` keyed by local id.
   *
   * Engine writes bypass this method — `recomputeDependents` in
   * `formula-engine.ts` calls `sourceCellData.set` directly so
   * cascades don't recurse through the variant layer or echo back
   * to the backend.
   */
  async #writeCellAndPersist<V extends CellValue = CellValue>(
    date: NaiveDate.Partial,
    value: V,
    cellId: Option<CellId>,
    actor: Option<Actor>,
  ): Promise<SourceRow.Persisted> {
    const backend = sheetOf(this).backend;
    const target = await this.#writeCellLocal(date, value, cellId, actor);
    if (target == null) return SourceRow.DEFERRED_TO_PROMOTE;
    await this.#persistNow(
      target,
      () => backend.writeCell(target.rowId, target.body, null, null),
      (refused) => this.#takeBackCell(refused),
    );
    return SourceRow.PERSISTED_BACKEND;
  }

  /**
   * The local half of every cell write: the optimistic store write (landed
   * synchronously, before the first `await`), the formula recompute, and —
   * on an ephemeral sheet — the copy-on-write claim. Resolves the backend
   * address to persist to, or `null` when there is nothing to send: an
   * ephemeral sheet has no server id (a NON-EMPTY edit claims it — the
   * promote carries the local write server-side; an empty one does not).
   */
  async #writeCellLocal<V extends CellValue = CellValue>(
    date: NaiveDate.Partial,
    value: V,
    cellId: Option<CellId>,
    actor: Option<Actor>,
  ): Promise<Option<CellTarget>> {
    // Before the paint: the cell's current value is what a refusal restores.
    const target = this.#beginCellWrite(date, cellId, value);
    try {
      return await this.#landCellWrite(target, value, actor);
    } catch (err) {
      this.#abandonCellWrite(target);
      throw err;
    }
  }

  /**
   * The local half of a cell write threw (the paint, the recompute, or an
   * ephemeral sheet's claim): its ledger write is refused, which releases
   * the hold it took on the row's store, and the cell shows what it showed
   * before (the write stayed open and the hold was kept).
   * The restore is not recomputed: the recompute is what may have thrown.
   * A write the ledger already accepted (an ephemeral sheet's, before its
   * claim) is not taken back: `refuse` answers `null` for a settled write.
   */
  #abandonCellWrite(target: CellTarget): void {
    const shown = this.#cellWrites.dated.refuse(target.write);
    if (shown != null) this.#paintCell(target.date, target.cellId, shown.value, SourceRow.ACTOR);
  }

  /**
   * `#writeCellLocal` after its ledger write opened: paint `value` into
   * `target`'s cell, recompute, and — on an ephemeral sheet — accept the
   * write and claim the sheet (resolving `null`: nothing to send).
   */
  async #landCellWrite<V extends CellValue = CellValue>(
    target: CellTarget,
    value: V,
    actor: Option<Actor>,
  ): Promise<Option<CellTarget>> {
    const { date, cellId } = target;
    const ck = this.#canonicalKey();
    if (ck != null) {
      this.#cells.canonicalCells.setCardinality(
        ck,
        this.descriptor.cellCardinality,
      );
      this.#cells.canonicalCells.set(ck, date, value, cellId, null, actor);
    } else {
      this.#cells.sourceCellData.set(
        this.id,
        date,
        value,
        cellId,
        null,
        actor,
      );
    }
    this.#cells.trackPromotionFlushCell(this.id, date, cellId);
    const { FormulaEngine } = await import("../../formula/formula-engine.ts");
    FormulaEngine.recomputeDependents(sheetOf(this), this.id, date.toRange());
    if (sheetOf(this).ephemeral) {
      // Nothing is sent, so nothing can refuse it: the paint stands.
      this.#cellWrites.dated.accept(target.write);
      const isEmpty = value === null || value === undefined || value === "";
      if (!isEmpty) await this.#cells.promoteHook()?.();
      return null;
    }
    return target;
  }

  /**
   * Open a cell write's record before its paint: the backend address, the
   * debounce key, and the write's place among the cell's writes still
   * waiting on a verdict (`CellWrites`), which remembers what the
   * cell showed before the first of them.
   */
  #beginCellWrite(
    date: NaiveDate.Partial,
    cellId: Option<CellId>,
    painted: CellValue,
  ): CellTarget {
    const ck = this.#canonicalKey();
    const rowId = ck != null ? RowId.of(ck) : RowId.fromLocalId(this.id);
    const key = this.#datedKey(date, cellId);
    const prior = this.#peekCell(date, cellId);
    const write = this.#cellWrites.dated.begin(key, prior, painted);
    const authority = this.#cells.sourceCellData.ordinaryStore(this.id);
    if (authority != null) this.#cellWrites.dated.hold(write, authority.retain(this.id));
    return {
      rowId,
      date,
      cellId,
      body: { date, value: painted, id: cellId ?? null, style: null },
      key,
      write,
    };
  }

  /** The ledger (and debounce) key of the date-keyed cell `(date, cellId)`. */
  #datedKey(date: NaiveDate.Partial, cellId: Option<CellId>): string {
    const ck = this.#canonicalKey();
    const rowId = ck != null ? RowId.of(ck) : RowId.fromLocalId(this.id);
    return CellWriteCoalescer.key(RowNetworkKey.of(rowId), `${date.toString()}:${cellId ?? ""}`);
  }

  /**
   * Take back a refused (or dropped) cell write: when it is the newest write
   * of the cell still waiting, the cell shows the next newest one — or, with
   * none left, what it showed before them — re-announced as
   * `SourceRow.ACTOR`, and dependents recompute. An older write's refusal
   * changes nothing on screen (a newer write stands). The store write is
   * synchronous; the returned promise is the recompute.
   */
  async #takeBackCell(target: CellTarget): Promise<void> {
    const shown = this.#cellWrites.dated.refuse(target.write);
    if (shown == null) return;
    this.#paintCell(target.date, target.cellId, shown.value, SourceRow.ACTOR);
    const { FormulaEngine } = await import("../../formula/formula-engine.ts");
    FormulaEngine.recomputeDependents(sheetOf(this), this.id, target.date.toRange());
  }

  /** Show `value` in a cell (`null`: clear it), stamped with `actor`. */
  #paintCell(
    date: NaiveDate.Partial,
    cellId: Option<CellId>,
    value: Option<CellValue>,
    actor: Actor,
  ): void {
    const ck = this.#canonicalKey();
    if (ck != null) {
      const store = this.#cells.canonicalCells;
      if (value == null) store.clear(ck, date, cellId, actor);
      else store.set(ck, date, value, cellId, null, actor);
      return;
    }
    const store = this.#cells.sourceCellData;
    if (value == null) store.clear(this.id, date, cellId, actor);
    else store.set(this.id, date, value, cellId, null, actor);
  }

  /**
   * Send a cell's persist now: cancel the cell's pending durable persist
   * (this write carries the newer value, so its verdict settles those writes
   * too) and resolve or reject on `send`'s verdict. On a refusal every write
   * it settles is taken back before the promise rejects — `target`'s own
   * through `takeBack` (`#takeBackCell`, or nothing for an event edit, whose
   * `#writeEvent` takes back all of its writes by event identity).
   */
  async #persistNow(
    target: CellTarget,
    send: () => Promise<unknown>,
    takeBack: (refused: CellTarget) => Promise<void>,
  ): Promise<void> {
    const coalescer = this.#cellWriteCoalescer;
    const cellWrites = this.#cellWrites;
    const superseded = cellWrites.takeDurable(target.key);
    coalescer.cancel(target.key);
    try {
      await send();
    } catch (err) {
      // The superseded writes are older: settle them first, so the
      // take-back lands once, on what the cell showed before all of them.
      CellWrites.settleDurable(superseded, { accepted: false, error: err });
      await takeBack(target);
      throw err;
    }
    this.#cellWrites.dated.accept(target.write);
    CellWrites.settleDurable(superseded, { accepted: true });
  }

  /**
   * Schedule a cell's persist on the sheet's trailing debounce (a burst of
   * keystrokes is one request) and resolve or reject on the verdict of the
   * request that carried it — the last one scheduled for the cell, or a
   * checked write that superseded it. A refused (or dropped) persist takes
   * the write back before `settled` rejects.
   */
  #persistDebounced(
    target: CellTarget,
    send: () => Promise<unknown>,
  ): Promise<void> {
    const coalescer = this.#cellWriteCoalescer;
    const cellWrites = this.#cellWrites;
    return new Promise<void>((resolve, reject) => {
      cellWrites.waitDurable(target.key, {
        settle: (verdict) => {
          if (verdict.accepted) {
            this.#cellWrites.dated.accept(target.write);
            resolve();
            return;
          }
          // The store write happens synchronously here (issue order across
          // the settled writes); the rejection waits for the recompute.
          const err = verdict.error;
          this.#takeBackCell(target).then(() => reject(err), () => reject(err));
        },
      });
      coalescer.scheduleOwned(
        sheetOf(this), target.key,
        () => {
          const carried = cellWrites.takeDurable(target.key);
          send().then(
            () => CellWrites.settleDurable(carried, { accepted: true }),
            (err: unknown) => CellWrites.settleDurable(carried, { accepted: false, error: err }),
          );
        },
        // Dropped unsent (`CellWriteCoalescer.clear`): nothing will answer,
        // so the writes it carried reject rather than wait forever.
        () =>
          CellWrites.settleDurable(
            cellWrites.takeDurable(target.key),
            {
              accepted: false,
              error: new SdkRequestError({
                boundary: "rows-http",
                step: "update",
                method: "POST",
                path: "/api/rows/{rowKey}/cells",
                failure: { outcome: "unaddressable" },
                details: "the pending cell persist was dropped before it was sent",
              }),
            },
          ),
      );
    });
  }

  /**
   * Checked cell write — used by facades to forward typed mutations
   * (e.g. `EventsFacade.addEvent`) to the underlying store. Paints the
   * value at once, then resolves `{ persisted: "backend" }` when the backend
   * accepted it; when it refused or never answered, the paint is taken back
   * (announced as `SourceRow.ACTOR`) and the promise rejects with the
   * backend's error (`RowsHttpBackendRequestError`, an `SdkRequestError`). On an
   * ephemeral sheet nothing is sent by this write: it
   * resolves `{ persisted: "deferred-to-promote" }` — a non-empty value
   * claims the sheet (once the promote hook returned, the promote carries
   * it); an empty one waits for a later promote (`SourceRow.Persisted`).
   * `actor` stamps the paint (`Actor.EXTERNAL` for a caller with no echo of
   * its own to recognise).
   *
   * Rejects rows whose cells are server-owned:
   *   - Rows flagged read-only via server-declared
   *     `metadata.readOnly = true` — the provider sync owns those
   *     cells (e.g. weather: `Row::READ_ONLY` on
   *     `tento-weather/src/connector/mod.rs`). A local write would
   *     be silently overwritten on the next refresh.
   *   - Formula rows — `FormulaDataSource` overrides `write` to
   *     reject; see `variants/formula-data-source.ts`.
   *
   * Other canonical rows (`:~google:…:events`, `:~lona:tasks`, …)
   * accept writes — the optimistic update lands on the shared
   * canonical store and persists through the backend via the
   * row's canonical lookup-key.
   */
  async #write<V extends CellValue = CellValue>(
    date: NaiveDate.Partial,
    value: V,
    cellId: Option<CellId>,
    actor: Actor,
  ): Promise<SourceRow.Persisted> {
    this.#assertWritable("write");
    return this.#writeCellAndPersist(date, value, cellId, actor);
  }

  /**
   * Durable cell write — the live-typing form of `write`. The value is
   * shown at once (`applied.kind === "shown"`); the persist rides the
   * sheet's trailing debounce, so a burst of keystrokes on one cell is one
   * request. `settled` resolves `{ persisted: "backend" }` when the request
   * that carried this write was accepted — `{ persisted:
   * "deferred-to-promote" }` on an ephemeral sheet, where nothing is sent
   * (see `write`) — and, when it was refused or unanswered, takes the
   * write back (as `write` does) and rejects with the backend's error; a
   * later `write` / `clear` of the same cell
   * supersedes the pending request and settles it with its own verdict.
   * The caller owns `settled` (an unhandled rejection is a bug).
   */
  #writeDurable<V extends CellValue = CellValue>(
    date: NaiveDate.Partial,
    value: V,
    cellId: Option<CellId>,
    actor: Actor,
  ): Durable<void, SourceRow.Persisted> {
    this.#assertWritable("write");
    const backend = sheetOf(this).backend;
    const settled = this.#writeCellLocal(date, value, cellId, actor).then(
      (target) =>
        target == null
          ? SourceRow.DEFERRED_TO_PROMOTE
          : this.#persistDebounced(target, () =>
            backend.writeCell(target.rowId, target.body, null, null)
          ).then(() => SourceRow.PERSISTED_BACKEND),
    );
    return { applied: { kind: "shown", value: undefined }, settled };
  }

  /**
   * Event write chokepoint — places an event in the lane its temporal kind
   * demands, matching the server's deconfliated representation: a TIMED event
   * is datetime-keyed in the custom lane, an ALL-DAY event is date-keyed in the
   * semantic lane. Both lanes key by the event's own id, so an all-day⇄timed
   * TOGGLE (or a day move) is a lane MOVE — clear the stale lane, write the new
   * — leaving exactly one entry per id. That is what makes the toggle
   * bug-class (a ghost cell stranded in the other lane) unrepresentable.
   *
   * `prev` is the event's prior state (the facade's indexed copy) on an
   * update / toggle / move, `null` on a fresh insert. The server re-derives the
   * stored cell key from the event payload (Point for timed, Range(ymd) for
   * all-day) and preserves the event id, so this optimistic local placement
   * matches what the next fetch returns — the seam that lets the cross-layer
   * mirror retire.
   *
   * Every edit places the event at exactly one day and lane, so its writes
   * settle by EVENT identity across days and lanes (`CellWrites.beginEvent`),
   * in issue order. A refused (or unanswered) edit takes back only its own
   * paint, then rejects with the backend's error: when it is the newest edit
   * of the event still waiting, each cell it wrote shows the newest write of
   * it still waiting, or what it showed before those writes
   * (`CellWrites`), announced as `SourceRow.ACTOR`; when a newer edit
   * of the event is still waiting, that edit's optimistic event stays shown —
   * the refused edit's writes leave the cells the newer edit also wrote, and
   * the newer edit takes over the others as "absent" (where it placed no
   * event), so its own later refusal restores what the cells showed before
   * both edits and its acceptance confirms them absent. An accepted edit
   * retires every older write of the same event still waiting in any other
   * day or lane: an older edit refused later can never restore a cell the
   * accepted one moved the event out of.
   */
  async #writeEvent(
    ev: CalendarEvent,
    prev: Option<CalendarEvent>,
    actor: Actor,
  ): Promise<void> {
    this.#assertWritable("write");
    const sheet = sheetOf(this);
    const nextKind = CellLane.temporalKindOfEvent(ev);
    const day = SourceRow.#eventDay(ev);
    // Every cell this edit paints, each opened in the ledger before it
    // paints: a toggle or a day move clears the previous lanes; a timed event
    // lands in the custom lane, an all-day one in its date cell.
    let clearsPrev = false;
    let prevDay: Option<NaiveDate.Partial> = null;
    if (prev != null) {
      const prevKind = CellLane.temporalKindOfEvent(prev);
      prevDay = SourceRow.#eventDay(prev);
      const movedDay = prevDay.toRange().start.dse !== day.toRange().start.dse;
      clearsPrev = prevKind !== nextKind || movedDay;
    }
    const timed: Option<CellEntry.Timepoint> = nextKind === "timepoint"
      ? { id: CellId.of(ev.id), dtr: new DateTime.Range(ev.start, ev.end), value: ev, style: null }
      : null;
    const custom = timed != null || clearsPrev ? this.#beginTimepointWrite(CellId.of(ev.id), timed) : null;
    const prevDate = clearsPrev && prevDay != null
      ? { date: prevDay, write: this.#beginCellWrite(prevDay, CellId.of(ev.id), null).write }
      : null;
    // The event's writes of this edit, by lane: what settles by event
    // identity against the event's other edits still waiting.
    const cellWrites = this.#cellWrites;
    const op = cellWrites.beginEvent(this.#timepointKey(CellId.of(ev.id)));
    if (custom != null) op.writes.push({ lane: "timepoint", write: custom, date: null });
    if (prevDate != null) op.writes.push({ lane: "dated", write: prevDate.write, date: prevDate.date });
    if (clearsPrev && prev != null) {
      // Before the new day's write opens (its prior is read after this clear). A throw here refuses
      // every write this edit opened so far.
      try {
        this.#clearEventLocal(prev, actor);
      } catch (err) {
        await this.#repaintEvent(ev.id, cellWrites.refuseEvent(op));
        throw err;
      }
    }
    const dayTarget = timed == null ? this.#beginCellWrite(day, CellId.of(ev.id), ev) : null;
    if (dayTarget != null) op.writes.push({ lane: "dated", write: dayTarget.write, date: day });
    try {
      if (timed != null) {
        // Optimistic local custom-lane write (datetime-keyed by the event id).
        // The POST carries the event payload, from which the server re-derives a
        // Point cell preserving the id — so no id reconciliation is needed.
        this.#setTimepointLocal(timed, actor);
        await this.#postEventCell(day, ev);
      } else if (dayTarget != null) {
        // All-day → the date-keyed semantic lane (the standard cell write
        // path); a refusal takes its paint back below, with the edit's other
        // writes, by event identity — never cell by cell.
        const target = await this.#landCellWrite(dayTarget, ev, actor);
        if (target != null) {
          await this.#persistNow(
            target,
            () => sheet.backend.writeCell(target.rowId, target.body, null, null),
            () => Promise.resolve(),
          );
        }
      }
    } catch (err) {
      await this.#repaintEvent(ev.id, cellWrites.refuseEvent(op));
      throw err;
    }
    await this.#repaintEvent(ev.id, cellWrites.acceptEvent(op));
  }

  /**
   * Show what a settled edit of event `eventId` left in each cell
   * (`CellWrites.Repaint`: `null` clears it), announced as
   * `SourceRow.ACTOR`, and recompute the dependents of each date cell.
   * A multi-cardinality row holds ONE date cell per id whatever its day, so
   * the date clears land first, and each only when the event's date cell
   * sits on that day — a clear of a day the event left never erases it
   * from the day it is shown on.
   */
  async #repaintEvent(eventId: string, repaints: readonly CellWrites.Repaint[]): Promise<void> {
    const sheet = sheetOf(this);
    const dated: { readonly date: NaiveDate.Partial; readonly value: Option<CellValue> }[] = [];
    for (const repaint of repaints) {
      if (repaint.lane === "timepoint") {
        this.#removeTimepointLocal(CellId.of(eventId), SourceRow.ACTOR);
        if (repaint.value != null) this.#setTimepointLocal(repaint.value, SourceRow.ACTOR);
      } else if (repaint.value == null) {
        dated.unshift(repaint);
      } else {
        dated.push(repaint);
      }
    }
    for (const { date, value } of dated) {
      const shownThere = this.#dateCells(date.toRange()).some((c) => c.id === eventId || c.id == null);
      if (value == null && !shownThere) continue;
      this.#paintCell(date, CellId.of(eventId), value, SourceRow.ACTOR);
      const { FormulaEngine } = await import("../../formula/formula-engine.ts");
      FormulaEngine.recomputeDependents(sheet, this.id, date.toRange());
    }
  }

  /**
   * Clear an event from the canonical store by id, from BOTH lanes — the
   * timepoint (custom) lane and the semantic date lane (`writeEvent`'s inverse).
   * Local-only. A cell sits in exactly one lane, but routing the clear solely by
   * `temporalKindOfEvent` misses a timed event stranded in the semantic lane
   * (lane drift on create/hydration), leaving it — and any expanded recurrence
   * occurrences derived from it — painted until reload. Clearing both lanes by
   * id is safe (the lane the cell is NOT in is a no-op) and self-healing;
   * `local.clearCell`'s clear is emit-always, so the read surface (the cell-backed
   * `EventsFacade`) is notified regardless of which lane held the cell. Mirrors
   * `EventsTable._clearCanonicalLane` (the SDK-side delete path).
   */
  #clearEventLocal(ev: CalendarEvent, actor: Actor): void {
    this.#removeTimepointLocal(CellId.of(ev.id), actor);
    this.#clearCellLocal(SourceRow.#eventDay(ev), CellId.of(ev.id), actor);
  }

  /** Day bucket an event's cell is keyed under — `startDate` for an all-day
   *  event, else the calendar day of `start`. */
  static #eventDay(ev: CalendarEvent): NaiveDate.Partial {
    return NaiveDate.Partial.fromDate(ev.startDate ?? ev.start.date);
  }

  /** POST + formula recompute for an event cell — shares `#writeCellAndPersist`'s tail
   *  without its semantic-lane local write (the custom lane was written
   *  optimistically by `writeEvent`). */
  async #postEventCell(
    day: NaiveDate.Partial,
    ev: CalendarEvent,
  ): Promise<void> {
    const ck = this.#canonicalKey();
    if (ck != null) {
      this.#cells.canonicalCells.setCardinality(
        ck,
        this.descriptor.cellCardinality,
      );
    }
    const { FormulaEngine } = await import("../../formula/formula-engine.ts");
    FormulaEngine.recomputeDependents(sheetOf(this), this.id, day.toRange());
    const backendRowId = ck != null
      ? RowId.of(ck)
      : RowId.fromLocalId(this.id);
    await sheetOf(this).backend.writeCell(backendRowId, {
      date: day,
      value: ev,
      id: CellId.of(ev.id),
      style: null,
    }, null, null);
  }

  /**
   * **Local only** — clear a cell from the store the read path resolves
   * from (`canonicalCells` for canonical rows, `sourceCellData` otherwise)
   * with no recompute and nothing sent: the event / task / slot clears whose
   * server delete flows through their own facade path. Rejects on the same
   * `metadata.readOnly` condition as `write`. `actor` stamps the clear.
   */
  #clearCellLocal(
    date: NaiveDate.Partial,
    cellId: Option<CellId>,
    actor: Actor,
  ): void {
    this.#assertWritable("clear");
    const ck = this.#canonicalKey();
    if (ck != null) {
      this.#cells.canonicalCells.clear(ck, date, cellId, actor);
    } else {
      this.#cells.sourceCellData.clear(this.id, date, cellId, actor);
    }
    this.#cells.trackPromotionFlushCell(this.id, date, cellId);
  }

  /**
   * Checked cell clear — the symmetric partner of `write`: clears locally,
   * recomputes dependents, then persists the clear and resolves
   * `{ persisted: "backend" }` on the backend's acceptance (a refusal puts
   * the value back, as `write`'s does, and rejects with its error), so
   * the clear sticks across reload. An
   * ephemeral sheet sends nothing (clearing never claims it) and resolves
   * `{ persisted: "deferred-to-promote" }`: the empty state round-trips
   * through serialize → hydrate on promote. `FormulaDataSource` rejects in
   * its override.
   */
  async #clear(
    date: NaiveDate.Partial,
    cellId: Option<CellId>,
    actor: Actor,
  ): Promise<SourceRow.Persisted> {
    const backend = sheetOf(this).backend;
    const target = await this.#clearCellLocalAndRecompute(date, cellId, actor);
    if (target == null) return SourceRow.DEFERRED_TO_PROMOTE;
    await this.#persistNow(
      target,
      () => backend.clearCell(target.rowId, date, cellId, null),
      (refused) => this.#takeBackCell(refused),
    );
    return SourceRow.PERSISTED_BACKEND;
  }

  /**
   * Durable cell clear — the live-typing form of `clear` (an emptied
   * editor): cleared at once (`applied.kind === "shown"`), persisted on the
   * sheet's trailing debounce; `settled` carries the verdict (and
   * `SourceRow.Persisted`) as `writeDurable`'s does.
   */
  #clearDurable(
    date: NaiveDate.Partial,
    cellId: Option<CellId>,
    actor: Actor,
  ): Durable<void, SourceRow.Persisted> {
    const backend = sheetOf(this).backend;
    const settled = this.#clearCellLocalAndRecompute(date, cellId, actor).then(
      (target) =>
        target == null
          ? SourceRow.DEFERRED_TO_PROMOTE
          : this.#persistDebounced(target, () =>
            backend.clearCell(target.rowId, date, cellId, null)
          ).then(() => SourceRow.PERSISTED_BACKEND),
    );
    return { applied: { kind: "shown", value: undefined }, settled };
  }

  /** `#clearCellLocal` + the recompute; the backend address, or `null` on an ephemeral sheet. */
  async #clearCellLocalAndRecompute(
    date: NaiveDate.Partial,
    cellId: Option<CellId>,
    actor: Actor,
  ): Promise<Option<CellTarget>> {
    // A read-only row throws before anything is recorded or cleared.
    this.#assertWritable("clear");
    const target = this.#beginCellWrite(date, cellId, null);
    try {
      this.#clearCellLocal(date, cellId, actor);
      const { FormulaEngine } = await import("../../formula/formula-engine.ts");
      FormulaEngine.recomputeDependents(sheetOf(this), this.id, date.toRange());
    } catch (err) {
      this.#abandonCellWrite(target);
      throw err;
    }
    if (sheetOf(this).ephemeral) {
      this.#cellWrites.dated.accept(target.write);
      return null;
    }
    return target;
  }

  /**
   * Queryable counterpart to `#assertWritable` — true when user /
   * facade cell writes are accepted. False for provider-owned rows
   * (`metadata.readOnly = true`); `FormulaDataSource` overrides to
   * false unconditionally. UI layers consult this to render cells
   * read-only instead of letting a write throw after the fact.
   */
  get cellsWritable(): boolean {
    const meta = this.#cells.rowMetadata.get(this.id).meta;
    return meta?.readOnly !== true;
  }

  /**
   * The hard backstop behind `cellsWritable`: a write or clear that reaches
   * a row whose cells take none throws. A row whose cells an engine owns
   * (`declareEngineOwnedCells`: a formula, a computed row) throws that
   * class's refusal; any other is a provider-owned read-only row.
   */
  /** The store-scoped cell write ledger shared by this row's sheet projections. */
  get #cellWriteCoalescer(): CellWriteCoalescer {
    return this.#cells.cellWriteCoalescer;
  }

  get #cellWrites(): CellWrites {
    return this.#cells.cellWrites;
  }

  #assertWritable(method: "write" | "clear"): void {
    if (this.cellsWritable) return;
    const engine = ENGINE_OWNED_CELLS.find((owner) => owner.owns(this));
    if (engine != null) throw new Error(engine.refusal(this, method));
    const name = this.label ?? this.id.rawId;
    const verb = method === "write" ? "writes are" : "clears are";
    throw new Error(
      `SourceRow.${method}: row "${name}" is marked read-only ` +
        `(metadata.readOnly = true) — ${verb} rejected because the ` +
        `row's cells are owned by a provider sync on lona-rows ` +
        `(see Row::READ_ONLY in tento-lona-connector and ` +
        `lona-rows/src/routes.rs).`,
    );
  }
}

/**
 * Where a cell write persists — the backend row, the body, the debounce key —
 * and the write's record among the cell's writes waiting on a verdict.
 */
type CellTarget = {
  readonly rowId: RowId;
  readonly date: NaiveDate.Partial;
  readonly cellId: Option<CellId>;
  readonly body: CellEntry;
  readonly key: string;
  readonly write: CellWrites.Write<Option<CellValue>>;
};

function encodingFromMetadata(metadata: Option<Json>): Option<string> {
  return Json.string(Json.object(metadata), "encoding");
}

/**
 * Declaration-merged namespace on the `SourceRow` class. Lets
 * call sites read the cell-data store as
 * `SourceRow.CellDataStore<K>` — the class itself lives in
 * `stores/source-row-cell-data-store.ts` to keep this file
 * focused on the base SourceRow shim. See that file's doc
 * comment for why the store exists.
 */
/**
 * **SDK-internal friend** (not on the barrel): install a batch-fetched cell
 * list on `row` for `range` — the row's private `installBatchedCellsSince`.
 * `since` is the write revisions captured when the request went out; `null`
 * means "now" (no local write can be newer). Tests reach it through
 * `@tento-lona/sheets/internal-testing`.
 */
/**
 * A source read refused or abandoned because what it was for is gone: its
 * sheet was retired, or the row was replaced or re-routed (another resident,
 * backend key, encoding or cardinality) while it was out. Not a transport
 * failure. It does not promise that nothing landed: an install discarded
 * part-way keeps the cells it had already installed, and a batched install
 * can be discarded after its coverage was noted; the read's caller learns
 * only that this read did not complete for the row it was for.
 */
export class SourceReadDiscardedError extends Error {
  constructor(readonly backendKey: RowNetworkKey) {
    super(`SourceRow read discarded: ${backendKey}`);
    this.name = "SourceReadDiscardedError";
  }
}

export function installBatchedCells(
  row: SourceRow,
  range: NaiveDate.Range,
  cells: readonly CellEntry[],
  metadata: Option<Json>,
  since: Option<SourceRow.WriteRevisions>,
): void {
  const sheet = sheetOf(row);
  const internals = sourceRowInternals.of(row).cells(row);
  // A composed local facade returns opaque paired-clock leases, so even an
  // immediate install must capture and release an exact pair.
  const captured = since == null ? internals.captureWriteRevisions() : null;
  const revisions = since ?? nonnull(captured);
  const finishers: Array<() => void> = [];
  try {
    const observers = sourceInstallObservers.get(sheet);
    if (observers != null) {
      const event: SourceInstallEvent = { rowId: row.id, range, cells: cells.length, since };
      for (const observer of [...observers]) {
        const finish = observer(event);
        if (typeof finish === "function") finishers.push(finish);
      }
    }
    sourceRowInternals.of(row).installBatched(row, range, cells, metadata, revisions);
  } finally {
    try { for (const finish of finishers) finish(); }
    finally { if (captured != null) internals.releaseWriteRevisions(captured); }
  }
}

/**
 * **Package-internal friend**: note `range` as read for `row` (a sheet-wide
 * read answered it; its cells are already installed), so the row's own
 * fetcher issues nothing for a range inside it (`SheetPrefetch.beginSheetRead`).
 */
export function noteSheetReadCoverage(row: SourceRow, range: NaiveDate.Range): void {
  sourceRowInternals.of(row).noteFetched(row, range);
}

/**
 * One batch install on a source row, as `installBatchedCells` reports it to
 * `observeSourceInstalls`: the row, the fetched range, how many cells the
 * batch carried, and the caller's write revisions (`null`: "now").
 */
export interface SourceInstallEvent {
  readonly rowId: RowLocalId;
  readonly range: NaiveDate.Range;
  readonly cells: number;
  readonly since: Option<SourceRow.WriteRevisions>;
}

/**
 * Called before each batch install on the sheet's source rows. A returned
 * function runs once the install has finished (thrown or not), so a harness
 * can bracket the install (e.g. attribute the store writes it causes).
 */
export type SourceInstallObserver = (event: SourceInstallEvent) => void | (() => void);

const sourceInstallObservers = new WeakMap<ModelSheet, Set<SourceInstallObserver>>();

/**
 * **White-box seam** (reached through the `sheetWiring` door, for the SDK's
 * instrumentation — `instrumentationOf` on `@tento-lona/internal-testing`): observe every batch install on
 * `sheet`'s source rows — the `since` and the plain path alike. The ux-test
 * multiplicity sampler counts `sourceInstall` events through it instead of
 * wrapping the private install method by name. Returns the unsubscribe.
 */
export function observeSourceInstalls(
  sheet: ModelSheet,
  observer: SourceInstallObserver,
): Unsubscribe {
  const set = sourceInstallObservers.get(sheet) ?? new Set<SourceInstallObserver>();
  sourceInstallObservers.set(sheet, set);
  const entry: SourceInstallObserver = (event) => observer(event);
  set.add(entry);
  return () => {
    set.delete(entry);
  };
}

/**
 * Types and values that go with `SourceRow`: `Writer`, `DateCell`,
 * `TimepointCell`, `Cell`, `tagged`, `WriteRevisions`, and 7 more.
 */
export namespace SourceRow {
  /**
   * A source row's writers, reached through `sourceWriterOf(row)` only (see
   * there). Each verb is the row's own, with the verdict semantics its doc
   * states: the checked writes (`write`, `clear`,
   * `writeTimepointCell`, `clearTimepointCell`, `writeEvent`) paint at once
   * and, on a refusal, take back their own paint before rejecting; the
   * durable twins carry the verdict on `settled`; the local writes
   * (`writer.local`) paint without sending anything.
   */
  export interface Writer {
    /** The row these writers write. */
    readonly row: SourceRow;
    /** Checked cell write (see the row's `write` notes in `source-row.ts`). */
    write<V extends CellValue = CellValue>(
      date: NaiveDate.Partial,
      value: V,
      cellId: Option<CellId>,
      actor: Actor,
    ): Promise<Persisted>;
    /** The live-typing form of `write`: shown at once, persisted on the sheet's debounce. */
    writeDurable<V extends CellValue = CellValue>(
      date: NaiveDate.Partial,
      value: V,
      cellId: Option<CellId>,
      actor: Actor,
    ): Durable<void, Persisted>;
    /** Checked cell clear — `write`'s partner. */
    clear(date: NaiveDate.Partial, cellId: Option<CellId>, actor: Actor): Promise<Persisted>;
    /** The live-typing form of `clear`. */
    clearDurable(
      date: NaiveDate.Partial,
      cellId: Option<CellId>,
      actor: Actor,
    ): Durable<void, Persisted>;
    /**
     * Create or replace (`replaceCellId`) a datetime cell, checked. The
     * server's answer is shown stamped with `actor`; a take-back with
     * `SourceRow.ACTOR`.
     */
    writeTimepointCell(
      dateRange: DateTime.Range<Utc>,
      data: Option<Json | CellValue>,
      replaceCellId: Option<CellId>,
      actor: Actor,
    ): Promise<void>;
    /**
     * Delete a datetime cell by id, checked. The delete and the server's
     * answer are shown stamped with `actor`; a take-back with `SourceRow.ACTOR`.
     */
    clearTimepointCell(cellId: CellId, actor: Actor): Promise<void>;
    /**
     * Move datetime cell `cellId` from `oldRange` to `newRange` with `data`,
     * checked: painted at once (stamped with `actor`), then sent as the
     * server's atomic replace, whose answer settles it — a refusal takes
     * back this move's own paint (announced as `SourceRow.ACTOR`) and
     * rejects with the backend's error.
     */
    moveTimepointCell(
      oldRange: DateTime.Range<Utc>,
      newRange: DateTime.Range<Utc>,
      cellId: CellId,
      data: Option<Json | CellValue>,
      actor: Actor,
    ): Promise<void>;
    /** Place an event in the lane its temporal kind demands, checked. */
    writeEvent(ev: CalendarEvent, prev: Option<CalendarEvent>, actor: Actor): Promise<void>;
    /** The local writes: they paint the row's store and send nothing (see `SourceRow.Writer.Local`). */
    readonly local: Writer.Local;
    /** Mark this row's cell writes in `range` consumed by derived readers. */
    markFreshInRange(range: NaiveDate.Range): void;
    /**
     * Re-read this row's cells over the days `range` spans (the fetch
     * coverage dropped, the range fetched again) — the recovery of a write
     * that failed but may have stored.
     */
    reread(range: DateTime.Range<Utc>): Promise<void>;
  }

  export namespace Writer {
    /**
     * A source row's local writes (`writer.local`): each paints the row's
     * store now, stamped with `actor`, and sends nothing — the event / task /
     * slot clears whose server delete flows through their own facade.
     */
    export interface Local {
      /** Clear a cell from the store the read path resolves from, with no recompute. */
      clearCell(date: NaiveDate.Partial, cellId: Option<CellId>, actor: Actor): void;
      /** Clear an event from both lanes by id. */
      clearEvent(ev: CalendarEvent, actor: Actor): void;
    }
  }

  /** A date-keyed cell: the value for a day/week/month/year column. */
  export type DateCell = CellEntry & { readonly kind: "date" };
  /** A timepoint cell: a value positioned at a `DateTime` range. */
  export type TimepointCell = CellEntry.Timepoint & { readonly kind: "timepoint" };
  /** One entry of `SourceRow.peekCells`, tagged by `kind`. */
  export type Cell = DateCell | TimepointCell;
  /** `SourceRow.countIn`: the cells of a range per lane. */
  export interface CellCount {
    /** Date-keyed cells (`peekDateCells`). */
    readonly date: number;
    /** Timepoint cells (`peekTimepointCells`). */
    readonly timepoint: number;
  }

  /** Tag the two lanes into one `Cell` list, date-keyed first. */
  export function tagged(
    dated: readonly CellEntry[],
    custom: readonly CellEntry.Timepoint[],
  ): Cell[] {
    const out: Cell[] = [];
    for (const entry of dated) out.push(Object.freeze({ ...entry, kind: "date" }));
    for (const entry of custom) out.push(Object.freeze({ ...entry, kind: "timepoint" }));
    return out;
  }

  /**
   * Local-write revisions of the two cell stores a row may install into,
   * captured when a gated response lands (candidate D). See
   * `CellDataStore.writtenLocallySince`.
   */
  export interface WriteRevisions {
    readonly canonical: number;
    readonly local: number;
  }

  export const CellDataStore: typeof CellDataStores.CellDataStore = CellDataStores.CellDataStore;
  export type CellDataStore<K> = CellDataStores.CellDataStore<K>;
  export type CellDataChange<K> = CellDataStores.CellDataChange<K>;
  export type CellDataChangeHandler<K> = CellDataStores.CellDataChangeHandler<K>;
  export type CellDataUnsubscribe = Unsubscribe;

  /**
   * Where a cell write (`write`, `clear`, and the `settled` of their
   * durable twins) left the value once it resolved:
   * - `"backend"`: this write was sent and the backend accepted it;
   * - `"deferred-to-promote"`: the sheet is ephemeral, so nothing was sent —
   *   the sheet's promote carries the value (a non-empty value claims the
   *   sheet; an empty one waits for a later promote). Not yet on the server.
   */
  export type Persisted = {
    readonly persisted: "backend" | "deferred-to-promote";
  };

  /** `Persisted` of a write the backend accepted. */
  export const PERSISTED_BACKEND: Persisted = deepFrozen({ persisted: "backend" });

  /** `Persisted` of a write on an ephemeral sheet (nothing sent). */
  export const DEFERRED_TO_PROMOTE: Persisted = deepFrozen({ persisted: "deferred-to-promote" });
}
